import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useStartSignupMutation } from "../../../../../service/customer-service-v2";
import { setSignUpId as setSignUpIdInStore } from "../../../../../stores/slices/sign-up-slice";
import { ApplicationRoutes } from "../../../../../routes";
import { trackEmailOtpSent, trackPasswordSet, trackSignupCompleted, trackSignupStarted } from "../../../../../services/business-analytics/onboardingEvents";
import { AuthPageLayout, FormWrapper } from "../../../components";
import { getStepConfig } from "./util";
import { FormData } from "./types";
import { Step1PersonalInfo } from "./Step1PersonalInfo";
import { Step2ContactInfo } from "./Step2ContactInfo";
import { Step3Password } from "./Step3Password";
import { Step4OTPVerification } from "./Step4OTPVerification";
import { Step5EnergyProfile } from "./Step5EnergyProfile";
import { Step5ProfilePicture } from "./Step5ProfilePicture";
import { Step6SharedAccount } from "./Step6SharedAccount";
import styles from "./multi-step-form.module.scss";

export type { FormData };

export interface MultiStepFormProps {
	onComplete?: (formData: FormData) => void;
	onBack?: () => void;
}

// Post-verification onboarding order mirrors the mobile app:
// 1-4 account setup + OTP, 5 Energy Profile, 6 Shared Account, 7 Profile Picture.
const TOTAL_STEPS = 7;

export const MultiStepForm: React.FC<MultiStepFormProps> = ({ onBack: onExternalBack }) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const [currentStep, setCurrentStep] = useState(1);
	const [signupId, setSignupId] = useState("");
	// Also guards against StrictMode's double effect run: a second signup_started would open a new flow instance.
	const signupStartedAt = useRef<number | null>(null);

	useEffect(() => {
		if (signupStartedAt.current !== null) return;
		signupStartedAt.current = Date.now();
		trackSignupStarted();
	}, []);

	const [signup, { isLoading: isSignupLoading }] = useStartSignupMutation();

	const {
		control,
		watch,
		setValue,
		trigger,
		getValues,
		formState: { errors },
	} = useForm<FormData>({
		mode: "onChange",
		defaultValues: {
			accountType: "Corporate",
			organizationName: "",
			firstName: "",
			lastName: "",
			referralCode: "",
			email: "",
			phone: "",
			password: "",
			confirmPassword: "",
			countryDialCode: "234",
		},
	});

	const accountType = watch("accountType");
	const password = watch("password");
	const email = watch("email");
	const phone = watch("phone");

	const stepConfig = getStepConfig(currentStep, email, phone);

	const cleanPhoneNumber = (rawPhone: string, dialCode: string): string => {
		const countryCode = `+${dialCode}`;
		if (rawPhone.startsWith(countryCode)) {
			const after = rawPhone.substring(countryCode.length).trim();
			if (after.startsWith("0")) return countryCode + after.substring(1);
		}
		return rawPhone;
	};

	const handleStep3Next = async () => {
		if (!(await trigger(["password", "confirmPassword"]))) return;

		const data = getValues();
		try {
			const response = await signup({
				email: data.email.trim(),
				password: data.password,
				firstName: data.firstName.trim(),
				lastName: data.lastName.trim(),
				phoneNumber: cleanPhoneNumber(data.phone, data.countryDialCode),
				customerType: data.accountType,
				// utilityId: process.env.REACT_APP_UTILITY_ID || "",
				...(data.accountType === "Corporate" && { organizationName: data.organizationName?.trim() }),
				...(data.referralCode && { referralCode: data.referralCode.trim() }),
			}).unwrap();
			trackPasswordSet("success");
			// Starting the sign-up is what sends the email OTP; there is no separate send call.
			trackEmailOtpSent(data.email.trim());
			setSignupId(response.signupId);
			dispatch(setSignUpIdInStore(response.signupId));
			setCurrentStep(4);
		} catch (err: any) {
			trackPasswordSet("failed");
			toast.error(err?.data?.title || t("common.labels.somethingWrong"));
		}
	};

	const handleBack = () => {
		if (currentStep <= 1) {
			onExternalBack?.();
		} else if (currentStep === 4) {
			setCurrentStep(3);
		} else {
			setCurrentStep(prev => prev - 1);
		}
	};

	return (
		<AuthPageLayout>
		<FormWrapper
			onBack={currentStep <= 4 ? handleBack : undefined}
			currentStep={currentStep}
			totalSteps={TOTAL_STEPS}
			showProgress
			showHelp
			title={stepConfig?.title}
			subtitle={stepConfig?.subtitle}
			isSignUp={true}
			onlyPrevious
			onStepChange={setCurrentStep}
		>
			<form className={styles.form} onSubmit={e => e.preventDefault()}>
				{currentStep === 1 && (
					<Step1PersonalInfo
						control={control}
						errors={errors}
						accountType={accountType}
						setValue={setValue}
						trigger={trigger}
						onNext={() => {
							setCurrentStep(2);
						}}
					/>
				)}
				{currentStep === 2 && <Step2ContactInfo control={control} errors={errors} setValue={setValue} trigger={trigger} onNext={() => setCurrentStep(3)} />}
				{currentStep === 3 && <Step3Password control={control} errors={errors} password={password} isLoading={isSignupLoading} onNext={handleStep3Next} />}
				{currentStep === 4 && (
					<Step4OTPVerification
						signupId={signupId}
						email={email}
						phone={phone}
						onComplete={() => {
							trackSignupCompleted(Math.round((Date.now() - (signupStartedAt.current ?? Date.now())) / 1000));
							setCurrentStep(5);
						}}
					/>
				)}
				{currentStep === 5 && <Step5EnergyProfile onComplete={() => setCurrentStep(6)} />}
				{currentStep === 6 && <Step6SharedAccount onComplete={() => setCurrentStep(7)} />}
				{currentStep === 7 && <Step5ProfilePicture onSkip={() => navigate(`../${ApplicationRoutes.DASHBOARD}`, { replace: true })} />}
			</form>
		</FormWrapper>
		</AuthPageLayout>
	);
};
